import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Spinner, Badge, Alert, Row, Col, Tabs, Tab, Table, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSkull,
    faVirus,
    faShieldAlt,
    faNewspaper,
    faBuilding,
    faFlag,
    faInfoCircle,
    faExclamationTriangle,
    faExclamationCircle,
    faChevronRight,
    faUsers,
    faLink,
    faEye,
    faChevronLeft,
    faStepBackward,
    faChevronDown,
    faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import {
    fetchThreatActorsByIndustryCountry,
    fetchMalwareByIndustryCountry,
    fetchTTPsByIndustryCountry,
    fetchThreatIntelFeeds,
    fetchSectorsAndCountries
} from '../../services/threatActorService';
import moment from 'moment';

const ThreatIntelDashboard = () => {
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Sector and Country dropdowns
    const [sectors, setSectors] = useState([]);
    const [countries, setCountries] = useState([]);
    const [selectedIndustry, setSelectedIndustry] = useState('Finance');
    const [selectedCountry, setSelectedCountry] = useState('IN');
    const [loadingDropdowns, setLoadingDropdowns] = useState(false);
    
    // State for data
    const [threatActors, setThreatActors] = useState([]);
    const [threatActorsLoading, setThreatActorsLoading] = useState(false);
    const [threatActorsError, setThreatActorsError] = useState(null);
    const [threatActorsTotal, setThreatActorsTotal] = useState(0);
    const [threatActorsCursor, setThreatActorsCursor] = useState(null);
    const [threatActorsHasNext, setThreatActorsHasNext] = useState(false);
    
    const [malware, setMalware] = useState([]);
    const [malwareLoading, setMalwareLoading] = useState(false);
    const [malwareError, setMalwareError] = useState(null);
    const [malwareTotal, setMalwareTotal] = useState(0);
    const [malwareCursor, setMalwareCursor] = useState(null);
    const [malwareHasNext, setMalwareHasNext] = useState(false);
    
    const [ttps, setTTPs] = useState([]);
    const [ttpsLoading, setTTPsLoading] = useState(false);
    const [ttpsError, setTTPsError] = useState(null);
    const [ttpsTotal, setTTPsTotal] = useState(0);
    const [ttpsCursor, setTTPsCursor] = useState(null);
    const [ttpsHasNext, setTTPsHasNext] = useState(false);
    
    const [feeds, setFeeds] = useState([]);
    const [feedsLoading, setFeedsLoading] = useState(false);
    const [feedsError, setFeedsError] = useState(null);
    const [feedsTotal, setFeedsTotal] = useState(0);
    
    // Pagination states
    const [activeTab, setActiveTab] = useState('threat-actors');
    const [threatActorsPage, setThreatActorsPage] = useState(1);
    const [malwarePage, setMalwarePage] = useState(1);
    const [ttpsPage, setTTPsPage] = useState(1);
    const [feedsPage, setFeedsPage] = useState(1);
    const itemsPerPage = 10;
    
    // Cursor history for pagination
    const [threatActorsCursors, setThreatActorsCursors] = useState([null]);
    const [malwareCursors, setMalwareCursors] = useState([null]);
    const [ttpsCursors, setTTPsCursors] = useState([null]);
    
    // Expanded descriptions state
    const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());

    const fetchThreatActorsData = async (cursor = null) => {
        setThreatActorsLoading(true);
        setThreatActorsError(null);
        try {
            const response = await fetchThreatActorsByIndustryCountry(selectedIndustry, selectedCountry, {
                count: itemsPerPage,
                orderBy: 'name',
                orderMode: 'desc',
                cursor: cursor
            });
            
            if (response.error) {
                throw new Error(response.userMessage || 'Failed to fetch threat actors');
            }
            
            setThreatActors(response.data?.threatActors || []);
            setThreatActorsTotal(response.data?.pagination?.total || 0);
            setThreatActorsCursor(response.data?.pagination?.endCursor || null);
            setThreatActorsHasNext(response.data?.pagination?.hasNextPage || false);
        } catch (err) {
            setThreatActorsError(err.message);
        } finally {
            setThreatActorsLoading(false);
        }
    };

    const fetchMalwareData = async (cursor = null) => {
        setMalwareLoading(true);
        setMalwareError(null);
        try {
            const response = await fetchMalwareByIndustryCountry(selectedIndustry, selectedCountry, {
                count: itemsPerPage,
                orderBy: 'name',
                orderMode: 'desc',
                cursor: cursor
            });
            
            if (response.error) {
                throw new Error(response.userMessage || 'Failed to fetch malware');
            }
            
            setMalware(response.data?.malware || []);
            setMalwareTotal(response.data?.pagination?.total || 0);
            setMalwareCursor(response.data?.pagination?.endCursor || null);
            setMalwareHasNext(response.data?.pagination?.hasNextPage || false);
        } catch (err) {
            setMalwareError(err.message);
        } finally {
            setMalwareLoading(false);
        }
    };

    const fetchTTPsData = async (cursor = null) => {
        setTTPsLoading(true);
        setTTPsError(null);
        try {
            const response = await fetchTTPsByIndustryCountry(selectedIndustry, selectedCountry, {
                count: itemsPerPage,
                orderBy: 'name',
                orderMode: 'desc',
                cursor: cursor
            });
            
            if (response.error) {
                throw new Error(response.userMessage || 'Failed to fetch TTPs');
            }
            
            setTTPs(response.data?.ttps || response.data?.attackPatterns || []);
            setTTPsTotal(response.data?.pagination?.total || 0);
            setTTPsCursor(response.data?.pagination?.endCursor || null);
            setTTPsHasNext(response.data?.pagination?.hasNextPage || false);
        } catch (err) {
            setTTPsError(err.message);
        } finally {
            setTTPsLoading(false);
        }
    };

    const fetchFeedsData = async (page = 1) => {
        setFeedsLoading(true);
        setFeedsError(null);
        try {
            const response = await fetchThreatIntelFeeds(page, itemsPerPage);
            
            if (response.error) {
                throw new Error(response.userMessage || 'Failed to fetch feeds');
            }
            
            const feedsData = response.data?.threatIntelFeeds || response.data?.feeds || response.data || [];
            setFeeds(Array.isArray(feedsData) ? feedsData : []);
            setFeedsTotal(response.data?.totalCount || feedsData.length);
        } catch (err) {
            setFeedsError(err.message);
        } finally {
            setFeedsLoading(false);
        }
    };

    // Fetch sectors and countries on mount
    useEffect(() => {
        const fetchDropdowns = async () => {
            setLoadingDropdowns(true);
            try {
                const response = await fetchSectorsAndCountries();
                if (!response.error && response.data) {
                    const sectorsList = response.data.sectors || [];
                    const countriesList = response.data.countries || [];
                    
                    // Sort sectors alphabetically
                    const sortedSectors = [...sectorsList].sort((a, b) => 
                        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
                    );
                    
                    // Sort countries: codes first (2-3 letters), then full names, both alphabetically
                    const sortedCountries = [...countriesList].sort((a, b) => {
                        const aIsCode = a.name.length <= 3 && /^[A-Z]{2,3}$/.test(a.name);
                        const bIsCode = b.name.length <= 3 && /^[A-Z]{2,3}$/.test(b.name);
                        
                        // Codes come first
                        if (aIsCode && !bIsCode) return -1;
                        if (!aIsCode && bIsCode) return 1;
                        
                        // Both same type, sort alphabetically
                        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
                    });
                    
                    setSectors(sortedSectors);
                    setCountries(sortedCountries);
                    
                    // Set default values if current selection doesn't exist
                    const financeExists = sortedSectors.some(s => s.name === 'Finance');
                    const inExists = sortedCountries.some(c => c.name === 'IN');
                    
                    if (!financeExists && sortedSectors.length > 0) {
                        setSelectedIndustry(sortedSectors[0].name);
                    }
                    if (!inExists && sortedCountries.length > 0) {
                        // Try to find "IN" or "India", otherwise use first country
                        const inCountry = sortedCountries.find(c => c.name === 'IN' || c.name === 'India');
                        setSelectedCountry(inCountry ? inCountry.name : sortedCountries[0].name);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch sectors and countries:', err);
            } finally {
                setLoadingDropdowns(false);
            }
        };
        fetchDropdowns();
    }, []);

    // Fetch all counts on initial load and when filters change
    useEffect(() => {
        const fetchAllCounts = async () => {
            setLoading(true);
            try {
                // Fetch all data to get counts - use count: 1 to minimize data transfer
                const [threatActorsRes, malwareRes, ttpsRes, feedsRes] = await Promise.all([
                    fetchThreatActorsByIndustryCountry(selectedIndustry, selectedCountry, { count: 1, orderBy: 'name', orderMode: 'desc' }),
                    fetchMalwareByIndustryCountry(selectedIndustry, selectedCountry, { count: 1, orderBy: 'name', orderMode: 'desc' }),
                    fetchTTPsByIndustryCountry(selectedIndustry, selectedCountry, { count: 1, orderBy: 'name', orderMode: 'desc' }),
                    fetchThreatIntelFeeds(1, 1)
                ]);
                
                // Set totals from responses
                if (!threatActorsRes.error) {
                    setThreatActorsTotal(threatActorsRes.data?.pagination?.total || 0);
                }
                if (!malwareRes.error) {
                    setMalwareTotal(malwareRes.data?.pagination?.total || 0);
                }
                if (!ttpsRes.error) {
                    setTTPsTotal(ttpsRes.data?.pagination?.total || 0);
                }
                if (!feedsRes.error) {
                    const feedsData = feedsRes.data?.threatIntelFeeds || feedsRes.data?.feeds || feedsRes.data || [];
                    setFeedsTotal(feedsRes.data?.totalCount || (Array.isArray(feedsData) ? feedsData.length : 0));
                }
                
                // Fetch full data for the active tab
                await fetchThreatActorsData();
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        
        if (selectedIndustry && selectedCountry) {
            fetchAllCounts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndustry, selectedCountry]);

    // Fetch detailed data when tab changes
    useEffect(() => {
        if (activeTab === 'threat-actors') {
            // Reset and fetch when filters change or tab is active
            setThreatActors([]);
            fetchThreatActorsData();
        } else if (activeTab === 'malware') {
            setMalware([]);
            fetchMalwareData();
        } else if (activeTab === 'ttps') {
            setTTPs([]);
            fetchTTPsData();
        } else if (activeTab === 'feeds') {
            setFeeds([]);
            fetchFeedsData(1);
        }
    }, [activeTab, selectedIndustry, selectedCountry]);

    const handleViewDetails = (id, type) => {
        if (type === 'threat-actor') {
            history.push(`/threat-intel/threat-actors/${id}`);
        }
    };

    const [selectedFeedDetails, setSelectedFeedDetails] = useState(null);
    const [showFeedDetailsModal, setShowFeedDetailsModal] = useState(false);

    const handleShowFeedDetails = (feed) => {
        setSelectedFeedDetails(feed);
        setShowFeedDetailsModal(true);
    };

    const handleNextPage = (type) => {
        if (type === 'threat-actors' && threatActorsCursor && threatActorsHasNext) {
            setThreatActorsCursors(prev => [...prev, threatActorsCursor]);
            setThreatActorsPage(prev => prev + 1);
            fetchThreatActorsData(threatActorsCursor);
        } else if (type === 'malware' && malwareCursor && malwareHasNext) {
            setMalwareCursors(prev => [...prev, malwareCursor]);
            setMalwarePage(prev => prev + 1);
            fetchMalwareData(malwareCursor);
        } else if (type === 'ttps' && ttpsCursor && ttpsHasNext) {
            setTTPsCursors(prev => [...prev, ttpsCursor]);
            setTTPsPage(prev => prev + 1);
            fetchTTPsData(ttpsCursor);
        } else if (type === 'feeds') {
            setFeedsPage(prev => prev + 1);
            fetchFeedsData(feedsPage + 1);
        }
    };

    const handlePreviousPage = (type) => {
        if (type === 'threat-actors' && threatActorsPage > 1) {
            const newCursors = [...threatActorsCursors];
            newCursors.pop();
            setThreatActorsCursors(newCursors);
            setThreatActorsPage(prev => prev - 1);
            const prevCursor = newCursors[newCursors.length - 1];
            fetchThreatActorsData(prevCursor);
        } else if (type === 'malware' && malwarePage > 1) {
            const newCursors = [...malwareCursors];
            newCursors.pop();
            setMalwareCursors(newCursors);
            setMalwarePage(prev => prev - 1);
            const prevCursor = newCursors[newCursors.length - 1];
            fetchMalwareData(prevCursor);
        } else if (type === 'ttps' && ttpsPage > 1) {
            const newCursors = [...ttpsCursors];
            newCursors.pop();
            setTTPsCursors(newCursors);
            setTTPsPage(prev => prev - 1);
            const prevCursor = newCursors[newCursors.length - 1];
            fetchTTPsData(prevCursor);
        } else if (type === 'feeds' && feedsPage > 1) {
            setFeedsPage(prev => prev - 1);
            fetchFeedsData(feedsPage - 1);
        }
    };

    const handleFirstPage = (type) => {
        if (type === 'threat-actors') {
            setThreatActorsCursors([null]);
            setThreatActorsPage(1);
            fetchThreatActorsData(null);
        } else if (type === 'malware') {
            setMalwareCursors([null]);
            setMalwarePage(1);
            fetchMalwareData(null);
        } else if (type === 'ttps') {
            setTTPsCursors([null]);
            setTTPsPage(1);
            fetchTTPsData(null);
        } else if (type === 'feeds') {
            setFeedsPage(1);
            fetchFeedsData(1);
        }
    };

    const extractCountries = (targetsCountries) => {
        if (!targetsCountries?.edges || targetsCountries.edges.length === 0) return [];
        return targetsCountries.edges.map(edge => edge.node?.to?.name).filter(Boolean);
    };

    const extractSectors = (targetsSectors) => {
        if (!targetsSectors?.edges || targetsSectors.edges.length === 0) return [];
        return targetsSectors.edges.map(edge => edge.node?.to?.name).filter(Boolean);
    };

    const extractMalware = (usesMalware) => {
        if (!usesMalware?.edges || usesMalware.edges.length === 0) return [];
        return usesMalware.edges.map(edge => edge.node?.to?.name).filter(Boolean);
    };

    const getConfidenceColor = (confidence) => {
        if (confidence >= 80) return '#dc3545';
        if (confidence >= 50) return '#ffc107';
        if (confidence >= 20) return '#0d6efd';
        return '#6c757d';
    };

    const getResourceLevelColor = (level) => {
        switch (level?.toLowerCase()) {
            case 'government': return 'danger';
            case 'individual': return 'warning';
            case 'organization': return 'info';
            default: return 'secondary';
        }
    };

    // Calculate statistics
    const totalThreats = threatActorsTotal + malwareTotal + ttpsTotal;

    const renderPagination = (type, currentPage, hasNext, total) => {
        const canGoBack = currentPage > 1;
        const canGoForward = hasNext;
        
        return (
            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div className="text-muted small">
                    Showing page {currentPage} of approximately {Math.ceil(total / itemsPerPage)} ({total} total items)
                </div>
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleFirstPage(type)}
                        disabled={!canGoBack}
                        title="First Page"
                    >
                        <FontAwesomeIcon icon={faStepBackward} />
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handlePreviousPage(type)}
                        disabled={!canGoBack}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                        Previous
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleNextPage(type)}
                        disabled={!canGoForward}
                    >
                        Next
                        <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid">
            <div className="content-wrapper">
                {/* Professional Header Section */}
                <div className="mb-4" style={{ 
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                    borderRadius: '16px',
                    padding: '32px',
                    color: 'white',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                }}>
                    <div className="d-flex justify-content-between align-items-start mb-4">
                        <div>
                            <h1 className="mb-2" style={{ 
                                fontSize: '2.5rem', 
                                fontWeight: '700',
                                color: 'white',
                                letterSpacing: '-0.5px'
                            }}>
                                Threat Intelligence Dashboard
                            </h1>
                            <div className="d-flex align-items-end gap-3 mb-3">
                                <div style={{ minWidth: '250px' }}>
                                    <label className="mb-2 d-block fw-semibold" style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.95)' }}>
                                        <FontAwesomeIcon icon={faBuilding} className="me-2" />
                                        Sector
                                    </label>
                                    <select
                                        className="form-select"
                                        value={selectedIndustry}
                                        onChange={(e) => {
                                            setSelectedIndustry(e.target.value);
                                            // Reset pagination when filter changes
                                            setThreatActorsPage(1);
                                            setThreatActorsCursors([null]);
                                            setMalwarePage(1);
                                            setMalwareCursors([null]);
                                            setTTPsPage(1);
                                            setTTPsCursors([null]);
                                        }}
                                        disabled={loadingDropdowns}
                                        style={{
                                            background: '#ffffff',
                                            border: '2px solid rgba(255,255,255,0.2)',
                                            color: '#212529',
                                            borderRadius: '10px',
                                            padding: '10px 40px 10px 15px',
                                            fontSize: '0.95rem',
                                            fontWeight: '500',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            cursor: loadingDropdowns ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!loadingDropdowns) {
                                                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        {loadingDropdowns ? (
                                            <option value="" style={{ color: '#6c757d' }}>Loading sectors...</option>
                                        ) : (
                                            sectors.map((sector) => (
                                                <option key={sector.id} value={sector.name} style={{ color: '#212529', padding: '8px' }}>
                                                    {sector.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div style={{ minWidth: '250px' }}>
                                    <label className="mb-2 d-block fw-semibold" style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.95)' }}>
                                        <FontAwesomeIcon icon={faFlag} className="me-2" />
                                        Country
                                    </label>
                                    <select
                                        className="form-select"
                                        value={selectedCountry}
                                        onChange={(e) => {
                                            setSelectedCountry(e.target.value);
                                            // Reset pagination when filter changes
                                            setThreatActorsPage(1);
                                            setThreatActorsCursors([null]);
                                            setMalwarePage(1);
                                            setMalwareCursors([null]);
                                            setTTPsPage(1);
                                            setTTPsCursors([null]);
                                        }}
                                        disabled={loadingDropdowns}
                                        style={{
                                            background: '#ffffff',
                                            border: '2px solid rgba(255,255,255,0.2)',
                                            color: '#212529',
                                            borderRadius: '10px',
                                            padding: '10px 40px 10px 15px',
                                            fontSize: '0.95rem',
                                            fontWeight: '500',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            cursor: loadingDropdowns ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!loadingDropdowns) {
                                                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        {loadingDropdowns ? (
                                            <option value="" style={{ color: '#6c757d' }}>Loading countries...</option>
                                        ) : (
                                            countries.map((country) => {
                                                const isCode = country.name.length <= 3 && /^[A-Z]{2,3}$/.test(country.name);
                                                return (
                                                    <option 
                                                        key={country.id} 
                                                        value={country.name} 
                                                        style={{ 
                                                            color: '#212529', 
                                                            padding: '8px',
                                                            fontFamily: isCode ? 'monospace' : 'inherit',
                                                            fontWeight: isCode ? '600' : '400',
                                                            letterSpacing: isCode ? '0.5px' : 'normal'
                                                        }}
                                                    >
                                                        {country.name}
                                                    </option>
                                                );
                                            })
                                        )}
                                    </select>
                                </div>
                            </div>
                            <p className="mb-0" style={{ 
                                fontSize: '1.1rem', 
                                color: 'rgba(255,255,255,0.85)',
                                fontWeight: '400'
                            }}>
                                Comprehensive threat landscape analysis for <strong style={{ color: 'white' }}>{selectedIndustry}</strong> sector in <strong style={{ color: 'white' }}>{selectedCountry}</strong>
                            </p>
                            <p className="mt-2 mb-0" style={{ 
                                fontSize: '0.9rem', 
                                color: 'rgba(255,255,255,0.7)'
                            }}>
                                Last updated: {moment().format('MMMM DD, YYYY [at] HH:mm')} UTC
                            </p>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-2">
                            <Badge bg="info" className="px-4 py-2" style={{ 
                                fontSize: '1rem',
                                fontWeight: '600',
                                borderRadius: '8px'
                            }}>
                                <FontAwesomeIcon icon={faBuilding} className="me-2" />
                                {selectedIndustry} Sector
                            </Badge>
                            <Badge bg="warning" className="px-4 py-2" style={{ 
                                fontSize: '1rem',
                                fontWeight: '600',
                                borderRadius: '8px'
                            }}>
                                <FontAwesomeIcon icon={faFlag} className="me-2" />
                                {selectedCountry}
                            </Badge>
                        </div>
                    </div>

                    {/* Key Metrics Row */}
                    <Row className="g-3 mt-3">
                        <Col md={3}>
                            <Card className="border-0" style={{ 
                                background: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px'
                            }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Total Threats
                                            </div>
                                            <div className="fw-bold" style={{ fontSize: '2rem', lineHeight: '1' }}>
                                                {totalThreats?.toLocaleString()}
                                            </div>
                                        </div>
                                        <div style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '12px',
                                            background: 'rgba(255,255,255,0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <FontAwesomeIcon icon={faExclamationCircle} style={{ fontSize: '1.5rem', color: '#ff6b6b' }} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0" style={{ 
                                background: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px'
                            }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Threat Actors
                                            </div>
                                            <div className="fw-bold" style={{ fontSize: '2rem', lineHeight: '1' }}>
                                                {threatActorsTotal.toLocaleString()}
                                            </div>
                                        </div>
                                        <div style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '12px',
                                            background: 'rgba(255,255,255,0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <FontAwesomeIcon icon={faSkull} style={{ fontSize: '1.5rem', color: '#ff6b6b' }} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0" style={{ 
                                background: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px'
                            }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Malware Families
                                            </div>
                                            <div className="fw-bold" style={{ fontSize: '2rem', lineHeight: '1' }}>
                                                {malwareTotal.toLocaleString()}
                                            </div>
                                        </div>
                                        <div style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '12px',
                                            background: 'rgba(255,255,255,0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <FontAwesomeIcon icon={faVirus} style={{ fontSize: '1.5rem', color: '#ffa726' }} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0" style={{ 
                                background: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px'
                            }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Attack Patterns
                                            </div>
                                            <div className="fw-bold" style={{ fontSize: '2rem', lineHeight: '1' }}>
                                                {ttpsTotal.toLocaleString()}
                                            </div>
                                        </div>
                                        <div style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '12px',
                                            background: 'rgba(255,255,255,0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '1.5rem', color: '#9c27b0' }} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>

                {error && (
                    <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}

                {/* Tab View */}
                <Card className="shadow-lg border-0" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="mb-0"
                        style={{
                            borderBottom: '2px solid #e9ecef'
                        }}
                    >
                        {/* Threat Actors Tab */}
                        <Tab
                            eventKey="threat-actors"
                            title={
                                <span className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faSkull} className="me-2" style={{ color: '#dc3545' }} />
                                    <span>Threat Actors</span>
                                    <Badge bg="danger" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                        {threatActorsTotal}
                                    </Badge>
                                </span>
                            }
                            style={{
                                borderTop: '4px solid #dc3545'
                            }}
                        >
                            <Card.Body className="p-4" style={{ background: '#fafbfc', minHeight: '500px' }}>
                                <div className="mb-4 p-3 rounded" style={{ 
                                    background: 'linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)',
                                    border: '1px solid #ffcccc'
                                }}>
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-danger me-2" />
                                        <strong className="text-dark" style={{ fontSize: '0.95rem' }}>Threat Actors Overview</strong>
                                    </div>
                                    <p className="text-muted small mb-0" style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
                                        Advanced Persistent Threat (APT) groups and cybercriminal organizations actively targeting financial institutions 
                                        and related entities in {selectedCountry}. These threat actors employ sophisticated techniques including social engineering, 
                                        zero-day exploits, and multi-stage attack campaigns. Click on any row to view detailed information about the threat actor.
                                    </p>
                                </div>
                                {threatActorsLoading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="danger" />
                                        <p className="text-muted mt-3">Loading threat actors...</p>
                                    </div>
                                ) : threatActorsError ? (
                                    <Alert variant="warning">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                        {threatActorsError}
                                    </Alert>
                                ) : threatActors.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '3rem', opacity: 0.3 }} />
                                        <p className="text-muted fw-semibold">No threat actors found</p>
                                    </div>
                                ) : (
                                    <>
                                        <Table hover responsive className="mb-0">
                                            <thead style={{ background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', color: 'white' }}>
                                                <tr>
                                                    <th style={{ color: 'white', fontWeight: '600', width: '20%' }}>Name</th>
                                                    <th style={{ color: 'white', fontWeight: '600', width: '25%' }}>Aliases</th>
                                                    <th style={{ color: 'white', fontWeight: '600', width: '45%' }}>Description</th>
                                                    <th style={{ color: 'white', fontWeight: '600', width: '10%' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {threatActors.map((actor) => {
                                                    const isExpanded = expandedDescriptions.has(actor.id);
                                                    const description = actor.description 
                                                        ? actor.description.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1').replace(/\*\*/g, '')
                                                        : null;
                                                    const shouldTruncate = description && description.length > 200;
                                                    
                                                    return (
                                                        <tr key={actor.id}>
                                                            <td className="fw-bold">{actor.name || 'N/A'}</td>
                                                            <td>
                                                                {actor.aliases && actor.aliases.length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {actor.aliases.slice(0, 3).map((alias, idx) => (
                                                                            <Badge key={idx} bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                                {alias}
                                                                            </Badge>
                                                                        ))}
                                                                        {actor.aliases.length > 3 && (
                                                                            <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                                +{actor.aliases.length - 3} more
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {description ? (
                                                                    <div>
                                                                        <div 
                                                                            style={{
                                                                                fontSize: '0.875rem',
                                                                                lineHeight: '1.6',
                                                                                whiteSpace: 'pre-wrap',
                                                                                wordWrap: 'break-word',
                                                                                color: '#495057',
                                                                                ...(shouldTruncate && !isExpanded ? {
                                                                                    display: '-webkit-box',
                                                                                    WebkitLineClamp: 3,
                                                                                    WebkitBoxOrient: 'vertical',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                } : {})
                                                                            }}
                                                                        >
                                                                            {description}
                                                                        </div>
                                                                        {shouldTruncate && (
                                                                            <button
                                                                                className="btn btn-link p-0 mt-2 text-primary"
                                                                                style={{ 
                                                                                    fontSize: '0.8rem', 
                                                                                    textDecoration: 'none',
                                                                                    border: 'none',
                                                                                    background: 'none',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    const newExpanded = new Set(expandedDescriptions);
                                                                                    if (isExpanded) {
                                                                                        newExpanded.delete(actor.id);
                                                                                    } else {
                                                                                        newExpanded.add(actor.id);
                                                                                    }
                                                                                    setExpandedDescriptions(newExpanded);
                                                                                }}
                                                                                onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.textDecoration = 'underline';
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.textDecoration = 'none';
                                                                                }}
                                                                            >
                                                                                {isExpanded ? (
                                                                                    <>
                                                                                        <FontAwesomeIcon icon={faChevronUp} className="me-1" style={{ fontSize: '0.7rem' }} />
                                                                                        Show less
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FontAwesomeIcon icon={faChevronDown} className="me-1" style={{ fontSize: '0.7rem' }} />
                                                                                        Show more
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">No description available</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleViewDetails(actor.id, 'threat-actor');
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                                                    View
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                        {renderPagination('threat-actors', threatActorsPage, threatActorsHasNext, threatActorsTotal)}
                                    </>
                                )}
                            </Card.Body>
                        </Tab>

                        {/* Malware Tab */}
                        <Tab
                            eventKey="malware"
                            title={
                                <span className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faVirus} className="me-2" style={{ color: '#fd7e14' }} />
                                    <span>Malware</span>
                                    <Badge bg="warning" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                        {malwareTotal}
                                    </Badge>
                                </span>
                            }
                            style={{
                                borderTop: '4px solid #fd7e14'
                            }}
                        >
                            <Card.Body className="p-4" style={{ background: '#fafbfc', minHeight: '500px' }}>
                                <div className="mb-4 p-3 rounded" style={{ 
                                    background: 'linear-gradient(135deg, #fff8f0 0%, #ffe8d6 100%)',
                                    border: '1px solid #ffd4a3'
                                }}>
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-warning me-2" />
                                        <strong className="text-dark" style={{ fontSize: '0.95rem' }}>Malware Families Overview</strong>
                                    </div>
                                    <p className="text-muted small mb-0" style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
                                        Malware families and variants actively used in campaigns against financial institutions in {selectedCountry}. 
                                        These include banking trojans, ransomware, information stealers, and advanced persistent malware 
                                        designed to evade detection and maintain long-term access. This table provides detailed information 
                                        about each malware family including aliases, types, and descriptions.
                                    </p>
                                </div>
                                {malwareLoading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="warning" />
                                        <p className="text-muted mt-3">Loading malware...</p>
                                    </div>
                                ) : malwareError ? (
                                    <Alert variant="warning">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                        {malwareError}
                                    </Alert>
                                ) : malware.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '3rem', opacity: 0.3 }} />
                                        <p className="text-muted fw-semibold">No malware found</p>
                                    </div>
                                ) : (
                                    <>
                                        <Table hover responsive className="mb-0">
                                            <thead style={{ background: 'linear-gradient(135deg, #fd7e14 0%, #e55a00 100%)', color: 'white' }}>
                                                <tr>
                                                    <th style={{ color: 'white', fontWeight: '600' }}>Name</th>
                                                    <th style={{ color: 'white', fontWeight: '600' }}>Aliases</th>
                                                    <th style={{ color: 'white', fontWeight: '600' }}>Type</th>
                                                    <th style={{ color: 'white', fontWeight: '600' }}>Family</th>
                                                    <th style={{ color: 'white', fontWeight: '600' }}>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {malware.map((item) => (
                                                    <tr key={item.id}>
                                                        <td className="fw-bold">{item.name}</td>
                                                        <td>
                                                            {item.aliases && item.aliases.length > 0 ? (
                                                                <small className="text-muted">
                                                                    {item.aliases.slice(0, 2).join(', ')}
                                                                    {item.aliases.length > 2 && ` +${item.aliases.length - 2}`}
                                                                </small>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {item.malware_types && item.malware_types.length > 0 ? (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {item.malware_types.slice(0, 2).map((type, idx) => (
                                                                        <Badge key={idx} bg="secondary" style={{ fontSize: '0.7rem' }}>
                                                                            {type}
                                                                        </Badge>
                                                                    ))}
                                                                    {item.malware_types.length > 2 && (
                                                                        <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                                                                            +{item.malware_types.length - 2}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {item.is_family ? (
                                                                <Badge bg="danger">
                                                                    <FontAwesomeIcon icon={faUsers} className="me-1" />
                                                                    Family
                                                                </Badge>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {item.description ? (() => {
                                                                const isExpanded = expandedDescriptions.has(item.id);
                                                                const description = item.description.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1').replace(/\*\*/g, '');
                                                                const shouldTruncate = description && description.length > 200;
                                                                
                                                                return (
                                                                    <div>
                                                                        <div 
                                                                            style={{
                                                                                fontSize: '0.875rem',
                                                                                lineHeight: '1.6',
                                                                                whiteSpace: 'pre-wrap',
                                                                                wordWrap: 'break-word',
                                                                                color: '#495057',
                                                                                maxWidth: '400px',
                                                                                ...(shouldTruncate && !isExpanded ? {
                                                                                    display: '-webkit-box',
                                                                                    WebkitLineClamp: 3,
                                                                                    WebkitBoxOrient: 'vertical',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                } : {})
                                                                            }}
                                                                        >
                                                                            {description}
                                                                        </div>
                                                                        {shouldTruncate && (
                                                                            <button
                                                                                className="btn btn-link p-0 mt-2 text-primary"
                                                                                style={{ 
                                                                                    fontSize: '0.8rem', 
                                                                                    textDecoration: 'none',
                                                                                    border: 'none',
                                                                                    background: 'none',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    const newExpanded = new Set(expandedDescriptions);
                                                                                    if (isExpanded) {
                                                                                        newExpanded.delete(item.id);
                                                                                    } else {
                                                                                        newExpanded.add(item.id);
                                                                                    }
                                                                                    setExpandedDescriptions(newExpanded);
                                                                                }}
                                                                                onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.textDecoration = 'underline';
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.textDecoration = 'none';
                                                                                }}
                                                                            >
                                                                                {isExpanded ? (
                                                                                    <>
                                                                                        <FontAwesomeIcon icon={faChevronUp} className="me-1" style={{ fontSize: '0.7rem' }} />
                                                                                        Show less
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FontAwesomeIcon icon={faChevronDown} className="me-1" style={{ fontSize: '0.7rem' }} />
                                                                                        Show more
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })() : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        {renderPagination('malware', malwarePage, malwareHasNext, malwareTotal)}
                                    </>
                                )}
                            </Card.Body>
                        </Tab>

                        {/* TTPs Tab */}
                        <Tab
                            eventKey="ttps"
                            title={
                                <span className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" style={{ color: '#6f42c1' }} />
                                    <span>TTPs</span>
                                    <Badge bg="info" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                        {ttpsTotal}
                                    </Badge>
                                </span>
                            }
                            style={{
                                borderTop: '4px solid #6f42c1'
                            }}
                        >
                            <Card.Body className="p-4" style={{ background: '#fafbfc', minHeight: '500px' }}>
                                <div className="mb-4 p-3 rounded" style={{ 
                                    background: 'linear-gradient(135deg, #f5f0ff 0%, #ede7f6 100%)',
                                    border: '1px solid #d1c4e9'
                                }}>
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
                                        <strong className="text-dark" style={{ fontSize: '0.95rem' }}>Tactics, Techniques & Procedures Overview</strong>
                                    </div>
                                    <p className="text-muted small mb-0" style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
                                        Attack patterns, techniques, and procedures mapped to the MITRE ATT&CK framework. These TTPs represent 
                                        the methods and behaviors used by threat actors when targeting financial institutions in {selectedCountry}, 
                                        including initial access, execution, persistence, and data exfiltration techniques. Each TTP includes 
                                        MITRE ATT&CK identifiers and external references for detailed analysis.
                                    </p>
                                </div>
                                {ttpsLoading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="info" />
                                        <p className="text-muted mt-3">Loading attack patterns...</p>
                                    </div>
                                ) : ttpsError ? (
                                    <Alert variant="warning">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                        {ttpsError}
                                    </Alert>
                                ) : ttps.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '3rem', opacity: 0.3 }} />
                                        <p className="text-muted fw-semibold">No TTPs found</p>
                                    </div>
                                ) : (
                                    <>
                                        <Table hover responsive className="mb-0">
                                            <thead style={{ background: 'linear-gradient(135deg, #6f42c1 0%, #5a32a3 100%)', color: 'white' }}>
                                                <tr>
                                                    <th style={{ color: 'white', fontWeight: '600' }}>Name</th>
                                                    <th style={{ color: 'white', fontWeight: '600' }}>MITRE ID</th>
                                                    <th style={{ color: 'white', fontWeight: '600' }}>Description</th>
                                                    <th style={{ color: 'white', fontWeight: '600' }}>References</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ttps.map((ttp) => (
                                                    <tr key={ttp.id}>
                                                        <td className="fw-bold">{ttp.name}</td>
                                                        <td>
                                                            {ttp.x_mitre_id ? (
                                                                <Badge bg="info">
                                                                    {ttp.x_mitre_id}
                                                                </Badge>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {ttp.description ? (() => {
                                                                const isExpanded = expandedDescriptions.has(ttp.id);
                                                                const description = ttp.description.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1').replace(/\*\*/g, '');
                                                                const shouldTruncate = description && description.length > 200;
                                                                
                                                                return (
                                                                    <div>
                                                                        <div 
                                                                            style={{
                                                                                fontSize: '0.875rem',
                                                                                lineHeight: '1.6',
                                                                                whiteSpace: 'pre-wrap',
                                                                                wordWrap: 'break-word',
                                                                                color: '#495057',
                                                                                maxWidth: '500px',
                                                                                ...(shouldTruncate && !isExpanded ? {
                                                                                    display: '-webkit-box',
                                                                                    WebkitLineClamp: 3,
                                                                                    WebkitBoxOrient: 'vertical',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                } : {})
                                                                            }}
                                                                        >
                                                                            {description}
                                                                        </div>
                                                                        {shouldTruncate && (
                                                                            <button
                                                                                className="btn btn-link p-0 mt-2 text-primary"
                                                                                style={{ 
                                                                                    fontSize: '0.8rem', 
                                                                                    textDecoration: 'none',
                                                                                    border: 'none',
                                                                                    background: 'none',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    const newExpanded = new Set(expandedDescriptions);
                                                                                    if (isExpanded) {
                                                                                        newExpanded.delete(ttp.id);
                                                                                    } else {
                                                                                        newExpanded.add(ttp.id);
                                                                                    }
                                                                                    setExpandedDescriptions(newExpanded);
                                                                                }}
                                                                                onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.textDecoration = 'underline';
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.textDecoration = 'none';
                                                                                }}
                                                                            >
                                                                                {isExpanded ? (
                                                                                    <>
                                                                                        <FontAwesomeIcon icon={faChevronUp} className="me-1" style={{ fontSize: '0.7rem' }} />
                                                                                        Show less
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FontAwesomeIcon icon={faChevronDown} className="me-1" style={{ fontSize: '0.7rem' }} />
                                                                                        Show more
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })() : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {ttp.externalReferences?.edges && ttp.externalReferences.edges.length > 0 ? (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {ttp.externalReferences.edges.slice(0, 2).map((ref, idx) => (
                                                                        ref.node?.url ? (
                                                                            <a
                                                                                key={idx}
                                                                                href={ref.node.url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="text-decoration-none"
                                                                            >
                                                                                <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                                                                                    {ref.node?.source_name || 'MITRE'}
                                                                                </Badge>
                                                                            </a>
                                                                        ) : (
                                                                            <Badge key={idx} bg="secondary" style={{ fontSize: '0.7rem' }}>
                                                                                {ref.node?.source_name || 'Reference'}
                                                                            </Badge>
                                                                        )
                                                                    ))}
                                                                    {ttp.externalReferences.edges.length > 2 && (
                                                                        <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                                                                            +{ttp.externalReferences.edges.length - 2}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        {renderPagination('ttps', ttpsPage, ttpsHasNext, ttpsTotal)}
                                    </>
                                )}
                            </Card.Body>
                        </Tab>

                        {/* Feeds Tab */}
                        <Tab
                            eventKey="feeds"
                            title={
                                <span className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faNewspaper} className="me-2" style={{ color: '#0d6efd' }} />
                                    <span>Intelligence Feeds</span>
                                    <Badge bg="primary" className="ms-2" style={{ fontSize: '0.7rem', color: 'white' }}>
                                        {feedsTotal}
                                    </Badge>
                                </span>
                            }
                            style={{
                                borderTop: '4px solid #0d6efd'
                            }}
                        >
                            <Card.Body className="p-4" style={{ background: '#fafbfc', minHeight: '500px' }}>
                                <div className="mb-4 p-3 rounded" style={{ 
                                    background: 'linear-gradient(135deg, #e6f2ff 0%, #cce5ff 100%)',
                                    border: '1px solid #99d1ff'
                                }}>
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
                                        <strong className="text-dark" style={{ fontSize: '0.95rem' }}>Threat Intelligence Feeds Overview</strong>
                                    </div>
                                    <p className="text-muted small mb-0" style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
                                        Curated threat intelligence feeds from trusted sources including security advisories, vulnerability disclosures, 
                                        threat actor activity reports, and industry-specific security alerts relevant to the {selectedIndustry} sector. 
                                        These feeds provide timely updates on emerging threats, vulnerabilities, and security incidents that may impact 
                                        financial institutions in {selectedCountry}. Click "Read" to access the full article.
                                    </p>
                                </div>
                                {feedsLoading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="text-muted mt-3">Loading intelligence feeds...</p>
                                    </div>
                                ) : feedsError ? (
                                    <Alert variant="warning">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                        {feedsError}
                                    </Alert>
                                ) : feeds.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '3rem', opacity: 0.3 }} />
                                        <p className="text-muted fw-semibold">No feeds available</p>
                                    </div>
                                ) : (
                                    <>
                                        <Table hover responsive className="mb-0">
                                            <thead style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)', color: 'white' }}>
                                                <tr>
                                                    <th style={{ color: 'white', fontWeight: '600', width: '25%' }}>Title</th>
                                                    <th style={{ color: 'white', fontWeight: '600', width: '15%' }}>Feed Source</th>
                                                    <th style={{ color: 'white', fontWeight: '600', width: '12%' }}>Published</th>
                                                    <th style={{ color: 'white', fontWeight: '600', width: '35%' }}>Summary</th>
                                                    <th style={{ color: 'white', fontWeight: '600', width: '13%' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {feeds.map((feed, index) => {
                                                    const feedId = feed._id || feed.id || `feed-${index}`;
                                                    const isExpanded = expandedDescriptions.has(feedId);
                                                    const summary = feed.summary || feed.description || '';
                                                    const shouldTruncate = summary && summary.length > 200;
                                                    const publishedAt = feed.publishedAt || feed.published;
                                                    const feedUrl = feed.feedUrl || feed.source;
                                                    
                                                    return (
                                                        <tr key={feedId}>
                                                            <td className="fw-bold">{feed.title || feed.name || 'Untitled Feed'}</td>
                                                            <td>
                                                                {feedUrl ? (
                                                                    <div>
                                                                        <Badge bg="info" style={{ fontSize: '0.75rem' }}>
                                                                            {feedUrl.length > 30 ? feedUrl.substring(0, 30) + '...' : feedUrl}
                                                                        </Badge>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {publishedAt ? (
                                                                    <small className="text-muted">
                                                                        {moment(publishedAt).format('MMM DD, YYYY')}
                                                                    </small>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {summary ? (
                                                                    <div>
                                                                        <div 
                                                                            style={{
                                                                                fontSize: '0.875rem',
                                                                                lineHeight: '1.6',
                                                                                whiteSpace: 'pre-wrap',
                                                                                wordWrap: 'break-word',
                                                                                color: '#495057',
                                                                                ...(shouldTruncate && !isExpanded ? {
                                                                                    display: '-webkit-box',
                                                                                    WebkitLineClamp: 3,
                                                                                    WebkitBoxOrient: 'vertical',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                } : {})
                                                                            }}
                                                                        >
                                                                            {summary}
                                                                        </div>
                                                                        {shouldTruncate && (
                                                                            <button
                                                                                className="btn btn-link p-0 mt-2 text-primary"
                                                                                style={{ 
                                                                                    fontSize: '0.8rem', 
                                                                                    textDecoration: 'none',
                                                                                    border: 'none',
                                                                                    background: 'none',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    const newExpanded = new Set(expandedDescriptions);
                                                                                    if (isExpanded) {
                                                                                        newExpanded.delete(feedId);
                                                                                    } else {
                                                                                        newExpanded.add(feedId);
                                                                                    }
                                                                                    setExpandedDescriptions(newExpanded);
                                                                                }}
                                                                                onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.textDecoration = 'underline';
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.textDecoration = 'none';
                                                                                }}
                                                                            >
                                                                                {isExpanded ? (
                                                                                    <>
                                                                                        <FontAwesomeIcon icon={faChevronUp} className="me-1" style={{ fontSize: '0.7rem' }} />
                                                                                        Show less
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FontAwesomeIcon icon={faChevronDown} className="me-1" style={{ fontSize: '0.7rem' }} />
                                                                                        Show more
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">No summary available</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    {feed.link && (
                                                                        <a
                                                                            href={feed.link}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <FontAwesomeIcon icon={faLink} className="me-1" />
                                                                            Read
                                                                        </a>
                                                                    )}
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleShowFeedDetails(feed);
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEye} className="me-1" />
                                                                        Details
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                        {renderPagination('feeds', feedsPage, feedsPage < Math.ceil(feedsTotal / itemsPerPage), feedsTotal)}
                                    </>
                                )}
                            </Card.Body>
                        </Tab>
                    </Tabs>
                </Card>

                {/* Feed Details Modal */}
                <Modal
                    show={showFeedDetailsModal}
                    onHide={() => setShowFeedDetailsModal(false)}
                    size="lg"
                    centered
                >
                    <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)', color: 'white' }}>
                        <Modal.Title style={{ color: 'white' }}>
                            <FontAwesomeIcon icon={faNewspaper} className="me-2" />
                            Threat Intelligence Feed Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {selectedFeedDetails && (
                            <div>
                                {/* Basic Information */}
                                <Card className="mb-3 border-0 shadow-sm">
                                    <Card.Header style={{ background: '#f8f9fa', fontWeight: '600' }}>
                                        Basic Information
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="mb-2">
                                            <Col md={3}><strong>Title:</strong></Col>
                                            <Col md={9}>{selectedFeedDetails.title || 'N/A'}</Col>
                                        </Row>
                                        <Row className="mb-2">
                                            <Col md={3}><strong>Link:</strong></Col>
                                            <Col md={9}>
                                                {selectedFeedDetails.link ? (
                                                    <a href={selectedFeedDetails.link} target="_blank" rel="noopener noreferrer" className="text-primary">
                                                        {selectedFeedDetails.link}
                                                        <FontAwesomeIcon icon={faLink} className="ms-1" style={{ fontSize: '0.7rem' }} />
                                                    </a>
                                                ) : 'N/A'}
                                            </Col>
                                        </Row>
                                        <Row className="mb-2">
                                            <Col md={3}><strong>Feed URL:</strong></Col>
                                            <Col md={9}>
                                                {selectedFeedDetails.feedUrl ? (
                                                    <a href={selectedFeedDetails.feedUrl} target="_blank" rel="noopener noreferrer" className="text-primary">
                                                        {selectedFeedDetails.feedUrl}
                                                        <FontAwesomeIcon icon={faLink} className="ms-1" style={{ fontSize: '0.7rem' }} />
                                                    </a>
                                                ) : 'N/A'}
                                            </Col>
                                        </Row>
                                        <Row className="mb-2">
                                            <Col md={3}><strong>Published:</strong></Col>
                                            <Col md={9}>
                                                {selectedFeedDetails.publishedAt ? moment(selectedFeedDetails.publishedAt).format('MMMM DD, YYYY [at] HH:mm') : 'N/A'}
                                            </Col>
                                        </Row>
                                        <Row className="mb-2">
                                            <Col md={3}><strong>Scanned At:</strong></Col>
                                            <Col md={9}>
                                                {selectedFeedDetails.scannedAt ? moment(selectedFeedDetails.scannedAt).format('MMMM DD, YYYY [at] HH:mm') : 'N/A'}
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={3}><strong>Status:</strong></Col>
                                            <Col md={9}>
                                                {selectedFeedDetails.threatIntelAnalysisProcessingStatus ? (
                                                    <Badge bg={selectedFeedDetails.threatIntelAnalysisProcessingStatus === 'COMPLETED' ? 'success' : 'warning'}>
                                                        {selectedFeedDetails.threatIntelAnalysisProcessingStatus}
                                                    </Badge>
                                                ) : 'N/A'}
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Summary */}
                                {selectedFeedDetails.summary && (
                                    <Card className="mb-3 border-0 shadow-sm">
                                        <Card.Header style={{ background: '#f8f9fa', fontWeight: '600' }}>
                                            Summary
                                        </Card.Header>
                                        <Card.Body>
                                            <p style={{ lineHeight: '1.6', marginBottom: 0 }}>
                                                {selectedFeedDetails.summary}
                                            </p>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Threat Intelligence Analysis Summary */}
                                {selectedFeedDetails.threatIntelAnalysisSummary && (
                                    <Card className="mb-3 border-0 shadow-sm">
                                        <Card.Header style={{ background: '#fff3cd', fontWeight: '600', color: '#856404' }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                            Threat Intelligence Analysis Summary
                                        </Card.Header>
                                        <Card.Body>
                                            <p style={{ lineHeight: '1.6', marginBottom: 0 }}>
                                                {selectedFeedDetails.threatIntelAnalysisSummary}
                                            </p>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Threat Intelligence Analysis Findings */}
                                {selectedFeedDetails.threatIntelAnalysisFindings && selectedFeedDetails.threatIntelAnalysisFindings.length > 0 && (
                                    <Card className="mb-3 border-0 shadow-sm">
                                        <Card.Header style={{ background: '#f8d7da', fontWeight: '600', color: '#721c24' }}>
                                            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                                            Threat Intelligence Analysis Findings
                                        </Card.Header>
                                        <Card.Body>
                                            {selectedFeedDetails.threatIntelAnalysisFindings.map((finding, idx) => (
                                                <div key={idx} className="mb-4 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                                                    <h6 className="mb-3 fw-bold">Finding #{idx + 1}</h6>
                                                    
                                                    <Row className="mb-2">
                                                        <Col md={4}><strong>Threat Actor:</strong></Col>
                                                        <Col md={8}>
                                                            {finding.threat_actor ? (
                                                                <Badge bg="danger">{finding.threat_actor}</Badge>
                                                            ) : (
                                                                <span className="text-muted">Not specified</span>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                    
                                                    <Row className="mb-2">
                                                        <Col md={4}><strong>Country of Origin:</strong></Col>
                                                        <Col md={8}>
                                                            {finding.country_of_origin ? (
                                                                <Badge bg="warning">{finding.country_of_origin}</Badge>
                                                            ) : (
                                                                <span className="text-muted">Not specified</span>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                    
                                                    <Row className="mb-2">
                                                        <Col md={4}><strong>Countries Targeted:</strong></Col>
                                                        <Col md={8}>
                                                            {finding.countries_targeted && finding.countries_targeted.length > 0 ? (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {finding.countries_targeted.map((country, cIdx) => (
                                                                        <Badge key={cIdx} bg="info">{country}</Badge>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">None specified</span>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                    
                                                    <Row className="mb-2">
                                                        <Col md={4}><strong>Industries Targeted:</strong></Col>
                                                        <Col md={8}>
                                                            {finding.industries_targeted && finding.industries_targeted.length > 0 ? (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {finding.industries_targeted.map((industry, iIdx) => (
                                                                        <Badge key={iIdx} bg="warning">{industry}</Badge>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">None specified</span>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                    
                                                    <Row className="mb-3">
                                                        <Col md={4}><strong>Types of Attack:</strong></Col>
                                                        <Col md={8}>
                                                            {finding.types_of_attack && finding.types_of_attack.length > 0 ? (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {finding.types_of_attack.map((attack, aIdx) => (
                                                                        <Badge key={aIdx} bg="danger">{attack}</Badge>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">None specified</span>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                    
                                                    {/* IOCs */}
                                                    {finding.iocs && finding.iocs.length > 0 && (
                                                        <div className="mt-3">
                                                            <strong className="mb-2 d-block">Indicators of Compromise (IOCs):</strong>
                                                            <div className="table-responsive">
                                                                <Table striped bordered hover size="sm">
                                                                    <thead style={{ background: '#e9ecef' }}>
                                                                        <tr>
                                                                            <th>Type</th>
                                                                            <th>Value</th>
                                                                            <th>Description</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {finding.iocs.map((ioc, iocIdx) => (
                                                                            <tr key={iocIdx}>
                                                                                <td>
                                                                                    <Badge bg="secondary">{ioc.ioc_type}</Badge>
                                                                                </td>
                                                                                <td>
                                                                                    <code style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                                                                        {ioc.ioc_value}
                                                                                    </code>
                                                                                </td>
                                                                                <td style={{ fontSize: '0.85rem' }}>
                                                                                    {ioc.ioc_description || 'N/A'}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </Card.Body>
                                    </Card>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowFeedDetailsModal(false)}>
                            Close
                        </Button>
                        {selectedFeedDetails?.link && (
                            <Button 
                                variant="primary" 
                                onClick={() => {
                                    window.open(selectedFeedDetails.link, '_blank');
                                }}
                            >
                                <FontAwesomeIcon icon={faLink} className="me-1" />
                                Open Article
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default ThreatIntelDashboard;
